// @givmo/passkeys — the words of the web's way back in (AS-WEB26) and of the company lock's refusal
// on a sign-in door (AS-WEB27), as the approved design page drew them (D-AS-48: "Approve all", the
// design as drawn and its drafts as written, version 5 of `scratchpad/2026-10-06-design-web-escape-
// hatch/review.html` in the Account Security & SSO plan folder).
//
// Status, per line (the page's string table):
//   APPROVED — every `ESCAPE_HATCH_WORDS` line. Reused verbatim from Jonathan's words or an
//              approved round: D-AS-39's match-requests line, D-AS-29's "Your personal account
//              stays yours…", D-AS-30's verb "Unlink", the web reset's step words (D-AS-35), which
//              are `FACTOR_RESET_STEP_WORDS`' own and are read from there, never restated here, and
//              the three lines the organization door uses, which the company SSO copy sweep
//              approved (D-AS-57: the entry, `backToSignIn`, `throttled`). The rest are the
//              Board-path round's (D-AS-68, Jonathan 2026-10-18 10:10 PDT, "2) approved"): `what`,
//              `actReminder` and `codeRejected` as it reworded them, the others as written.
//   DRAFT    — `LOCK_REFUSAL_WORDS` only: the lock's refusal on a door (AS-WEB27) is the sign-in
//              lock's, and waits for the lock's own sweep. D-AS-48 (4) approved them as written,
//              kept DRAFT until then.
//
// Three rules the words keep (the apps' own, `CompanySignInCopy.swift`'s header):
//   1. Signed out, never name the employer: whoever holds the password reads it.
//   2. Never confirm or deny that an account exists: the code request is a 202 for every address,
//      so every code line holds "if that address has a Givmo account".
//   3. State the cost before the act, and the non-destructive way first: a current employee must not
//      be steered into unlinking because the page had nothing else to offer.
//
// "Company" stays a company word here while only a company can require its sign-in (company SSO ships
// first, D-AS-41); the door's label is the organization word (`organization-sso-copy.ts`).

import { SIGN_IN_LOCK_LINE, SIGN_IN_LOCK_TITLE } from '@givmo/auth';
import { FACTOR_RESET_STEP_WORDS } from './factor-reset-copy';

/** The entry link on each door, and the flow's first title (APPROVED (D-AS-57): the apps' entry). */
export const ESCAPE_HATCH_ENTRY = 'Lost access to your company sign-in?';

/** The way back in: its three steps and their states. */
export const ESCAPE_HATCH_WORDS = {
  // ── Step 1: what it does and costs, before anything is asked; then the account's address ──
  title: ESCAPE_HATCH_ENTRY,
  /**
   * APPROVED (D-AS-68 decision 1): iOS escapeHatchIntro's first two sentences, who it is for and
   * then what it does, with D-AS-30's "unlink".
   */
  what: 'Only use this if you can’t sign in to Givmo any other way. We can unlink your company from your Givmo account, so your personal sign-in works again.',
  /** APPROVED (D-AS-68): Android auth_escape_hatch_cost, with "unlinking". */
  cost: 'Unlinking ends your employee connection: you’ll lose employee-only content and any benefits your employer offers through Givmo.',
  /** APPROVED (D-AS-39, copy round 3). */
  matchRequests: 'Any pending match requests are cancelled too.',
  /** APPROVED (D-AS-29 decision 6). */
  keep: 'Your personal account stays yours: if you unlink your company, you keep everything else.',
  /** APPROVED (D-AS-35). */
  emailLabel: FACTOR_RESET_STEP_WORDS.emailLabel,
  /** APPROVED (D-AS-68): Android auth_escape_hatch_email_hint, verbatim. */
  emailHint: 'Use the email on your Givmo account, not your work email.',
  /** APPROVED (D-AS-35). */
  request: FACTOR_RESET_STEP_WORDS.request,
  /** APPROVED (D-AS-35). */
  haveCode: FACTOR_RESET_STEP_WORDS.haveCode,
  /** APPROVED (D-AS-57): both apps' escape ("Back to sign in"). */
  backToSignIn: 'Back to sign in',

  // ── Step 2: the emailed code, the act restated, the act ──
  /** APPROVED (D-AS-35). */
  codeTitle: FACTOR_RESET_STEP_WORDS.verifyTitle,
  /** APPROVED (D-AS-68): the apps' sent line, naming the typed address so a typo shows. */
  codeSent: (email: string) => `If ${email} has a Givmo account, we’ve emailed it a 6-digit code.`,
  /**
   * APPROVED (D-AS-68): the code step reached by "I already have a code", where nothing was just
   * sent.
   */
  codeFromEmail: 'Enter the 6-digit code from your email.',
  /** APPROVED (D-AS-35). */
  codeLabel: FACTOR_RESET_STEP_WORDS.codeLabel,
  /** APPROVED (D-AS-35). */
  resend: FACTOR_RESET_STEP_WORDS.resend,
  /**
   * APPROVED (D-AS-68 decision 2): the backend ignores a request inside 60 s of the last code.
   * `wait` is "m:ss".
   */
  resendWait: (wait: string) => `You can ask for a new code in ${wait}.`,
  /** APPROVED (D-AS-68 decision 2): uniform, like the sent line. */
  resent: 'If that address has a Givmo account, we’ve emailed it a new code. Use the newest one.',
  /** APPROVED (D-AS-68 fix 6): the act and what it keeps, right above its button. */
  actReminder: 'This unlinks your company right away. Your personal account stays yours.',
  /** APPROVED (D-AS-68): the act's button, with D-AS-30's verb. */
  act: 'Unlink my company',
  /** APPROVED (D-AS-35). */
  back: FACTOR_RESET_STEP_WORDS.back,

  // ── Done: true whether or not there was a company (the backend's 204 is uniform) ──
  /** APPROVED (D-AS-68). */
  doneTitle: 'You can sign in again',
  /**
   * APPROVED (D-AS-68): the apps' done line, reworded to stay true when there was nothing to
   * unlink.
   */
  doneBody:
    'Your Givmo account isn’t linked to a company now, so your personal sign-in works. Sign in the way you did before.',

  // ── The states (APPROVED: D-AS-68, and D-AS-57 for `throttled`) ──
  /** A wrong, expired or spent code: the backend's one uniform 400 (D-AS-68 fix 5). */
  codeRejected: 'That code didn’t work. Check it and try again, or get a new code.',
  /** APPROVED (D-AS-57): a 429, at sending or at the code. "An hour" is the AUTHN_RECOVERY_* window. */
  throttled: 'Too many tries for now. Wait up to an hour, then try again.',
  /** No answer, or a server error: nothing was learned. */
  unavailable: 'We couldn’t do that just now. Try again in a few minutes.',
  /** The absent-route 404: no sign-in lock exists here now, so signing in is the likely way. */
  dark: 'This isn’t available right now. Go back and try signing in again.',
} as const;

/**
 * The lock's refusal on a door (AS-WEB27), in place of the red error line. The door's button between
 * the two paragraphs is the organization door's own label (`organization-sso-copy.ts`).
 */
export const LOCK_REFUSAL_WORDS = {
  /**
   * DRAFT: the signed-out twin of the apps' signed-in "{company} manages how you sign in". The
   * error model's own words (`@givmo/auth`), so a surface with no notice says the same.
   */
  title: SIGN_IN_LOCK_TITLE,
  /** DRAFT: iOS lockBody's first sentence; the web has its own company door, so no "Givmo app". */
  body: SIGN_IN_LOCK_LINE,
  /** DRAFT: iOS lockBody's second sentence, with D-AS-30's "unlink". */
  escape:
    'If you no longer work there, or your work account isn’t set up for Givmo, you can unlink your company and get back into your personal account.',
  /** DRAFT: the verb is D-AS-30's (APPROVED). */
  unlink: 'Unlink your company',
} as const;
